import React from "react";

import { Theme, ThemeOptions, createTheme } from "@mui/material";

import { ExpandLessRounded } from "@mui/icons-material";

import { figmaColors, figmaNewColors } from "./colors";

/**
 * Parts of the theme that are specific to each restaurant's brand.
 */
export type BrandedThemeProps = {
  // colors
  backgroundColor?: string;
  accentColor?: string;
  accentContrastColor?: string;
};

/**
 * Igfd wide theme props.
 */
export type IgfdThemeProps = BrandedThemeProps & {
  mode?: "light" | "dark";
};

export const createDefaultTheme = ({
  // currently mode is not used, but kept here as something
  // we might want to use in the future.
  mode = "light",
  backgroundColor,
  accentColor,
  accentContrastColor,
}: IgfdThemeProps): Theme => {
  const radius = 6;
  const buttonRadius = "1.5rem";

  const original = createTheme({
    palette: {
      mode,
      background: {
        default: backgroundColor
          ? backgroundColor
          : mode === "dark"
          ? figmaColors.foundation
          : figmaColors.clear,
        paper: mode === "dark" ? figmaColors.foundation : figmaColors.clear,
      },
      primary: {
        main: accentColor || figmaColors.accent,
        contrastText: accentContrastColor || figmaColors.clear,
      },
      secondary: {
        main: figmaColors.discover,
        contrastText: figmaColors.clear,
      },
      error: {
        light: figmaColors.allergy,
        main: figmaColors.allergySoft,
        dark: figmaColors.allergyHard,
      },
      warning: {
        light: figmaColors.avoid,
        main: figmaColors.avoidSoft,
      },
      info: {
        extraLight: figmaColors.discoverExtraLight,
        light: figmaColors.info,
        main: figmaColors.infoSoft,
        contrastText: figmaColors.foundation,
      },
      success: {
        light: figmaColors.recommended,
        main: figmaColors.recommendedSoft,
        dark: figmaColors.accent,
      },
      grey: {
        50: figmaColors.clear,
        100: figmaColors.fade,
        200: figmaColors.fade,
        300: figmaColors.smooth,
        400: figmaColors.smooth,
        500: figmaColors.silver,
        600: figmaColors.silver,
        700: figmaColors.shadow,
        800: figmaColors.shadow,
        900: figmaColors.foundation,
        A100: figmaColors.fade,
        A200: figmaColors.smooth,
        A400: figmaColors.silver,
        A700: figmaColors.shadow,
      },
      text: {
        primary: figmaColors.foundation,
        secondary: figmaColors.discover,
        disabled: figmaColors.silver,
      },
      common: {
        black: figmaColors.foundation,
        white: figmaColors.clear,
      },
      // colors from figma
      discover: {
        main: figmaColors.discover,
        extraLight: figmaColors.discoverExtraLight,
      },
      allergy: {
        main: figmaColors.allergy,
        soft: figmaColors.allergySoft,
        hard: figmaColors.allergyHard,
      },
      avoid: {
        main: figmaColors.avoid,
        soft: figmaColors.avoidSoft,
      },
      recommended: {
        main: figmaColors.recommended,
        soft: figmaColors.recommendedSoft,
      },
      accent: figmaColors.accent,
      extras: {
        discoverXXLight: figmaColors.extras.discoverXXLight,
        warningXXLight: figmaColors.extras.warningXXLight,
      },
      // realigned color pallette
      oscar: {
        light: {
          borders: {
            dark: figmaNewColors.netural[700],
            gray: figmaNewColors.netural[300],
            electric: figmaNewColors.electricBlue[900],
          },
          content: {
            content1: figmaNewColors.darkGrey[700],
            content2: figmaNewColors.netural[900],
            content3: figmaNewColors.netural[500],
            link: figmaNewColors.electricBlue[900],
            clear: figmaNewColors.netural[50],
            danger: figmaNewColors.red[800],
            warning: figmaNewColors.orange[900],
            informative: figmaNewColors.blue[800],
            takeNote: figmaNewColors.electricBlue[600],
            success: figmaNewColors.green[900],
          },
          icon: {
            black: figmaNewColors.darkGrey[700],
            dark: figmaNewColors.netural[900],
            mid: figmaNewColors.netural[400],
            light: figmaNewColors.netural[50],
            link: figmaNewColors.electricBlue[900],
            danger: figmaNewColors.red[900],
            warning: figmaNewColors.yellow[900],
            informative: figmaNewColors.blue[900],
            approval: figmaNewColors.green[600],
            takeNote: figmaNewColors.electricBlue[300],
            accent: figmaNewColors.green[900],
          },
          surface: {
            clear: figmaNewColors.netural[50],
            neutral: figmaNewColors.netural[100],
            gray: figmaNewColors.netural[300],
            takeNote: figmaNewColors.electricBlue[50],
            informative: figmaNewColors.blue[50],
            warning: figmaNewColors.yellow[50],
            danger: figmaNewColors.red[100],
            safe: figmaNewColors.green[100],
            electric: figmaNewColors.electricBlue[900],
            dark: figmaNewColors.netural[700],
            black: figmaNewColors.darkGrey[700],
            bold: figmaNewColors.netural[400],
            announcement: figmaNewColors.purple[500],
          },
          button: {
            primary: figmaNewColors.green[900],
            mid: figmaNewColors.netural[400],
            secondary: figmaNewColors.electricBlue[900],
            clear: figmaNewColors.netural[50],
            dark: figmaNewColors.darkGrey[100],
            danger: figmaNewColors.red[900],
          },
          interactive: {
            hover1: figmaNewColors.netural[100],
          },
          data: {
            positive: figmaNewColors.green[600],
            positiveLight: figmaNewColors.green[100],
            negative: figmaNewColors.orange[900],
            negativeLight: figmaNewColors.orange[50],
            neutral: figmaNewColors.electricBlue[900],
            neutralLight: figmaNewColors.electricBlue[50],
            neutralPale: figmaNewColors.electricBlue[300],
            midway: figmaNewColors.blue[400],
          },
          tag: {
            content: figmaNewColors.electricBlue[500],
            background: figmaNewColors.electricBlue[50],
            highlight: figmaNewColors.yellow[600],
          },
        },
      },
    },
    shape: {
      borderRadius: radius,
    },
    typography: {
      fontFamily: "'Avenir medium', Helvetica",
      fontWeightMedium: 400,
      /* Heading three */
      h4: {
        fontFamily: "'Avenir roman', Helvetica",
        fontWeight: 400,
        fontSize: 20,
        lineHeight: 1.4, // 28px
      },
      /* Heading two */
      h3: {
        fontFamily: "'Avenir roman', Helvetica",
        fontWeight: 400,
        fontSize: 24,
        lineHeight: 1.333, // 32px
      },
      /* Heading one */
      h2: {
        fontFamily: "'Avenir light', Helvetica",
        fontWeight: 400,
        fontSize: 32,
        lineHeight: 1.25, // 40 px
      },
      /* Display */
      h1: {
        fontFamily: "'Avenir light', Helvetica",
        fontWeight: 400,
        fontSize: 64,
        lineHeight: 1.125, // 72px
      },
      /* Body L */
      body1: {
        fontFamily: "'Avenir book', Helvetica",
        fontWeight: 400,
        fontSize: 16,
        lineHeight: 1.5, // 24px
      },
      /* Body M */
      body2: {
        fontFamily: "'Avenir book', Helvetica",
        fontWeight: 400,
        fontSize: 14,
        lineHeight: 1.429, // 20px
      },
      /* Label L */
      subtitle1: {
        fontFamily: "'Avenir medium', Helvetica",
        fontWeight: 400,
        fontSize: 16,
        lineHeight: 1.5, // 24px
      },
      /* OSCAR */
      displayRLight: {
        fontFamily: "avenir_lt_std95_black",
        fontSize: 20,
        lineHeight: 1.429, // 20px
      },
      headingLMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 32,
        lineHeight: 1.35,
      },
      headingLLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 32,
        lineHeight: 1.35,
      },
      headingRMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 24,
        lineHeight: 1.45,
      },
      headingRLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 24,
        lineHeight: 1.35,
      },
      headingSMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 28,
        lineHeight: 1.35,
      },
      headingSLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 28,
        lineHeight: 1.35,
      },
      labelLHeavy: {
        fontFamily: "avenir_lt_black !important",
        fontSize: 16,
        lineHeight: 1.35,
      },
      labelRHeavy: {
        fontFamily: "avenir_lt_black !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      labelLMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 16,
        lineHeight: 1.35,
      },
      labelRMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      labelRLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      labelSMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 12,
        lineHeight: 1.35,
      },
      labelXSMedium: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 12,
        lineHeight: 1.45,
      },
      labelXSLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 12,
        lineHeight: 1.45,
      },
      labelM: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      labelS: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 12,
        lineHeight: 1.35,
      },
      inputR: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      bodyLLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 16,
        lineHeight: 1.35,
      },
      bodyRLight: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 14,
        lineHeight: 1.35,
      },
      buttonL: {
        fontFamily: "avenir_lt_medium !important",
        fontSize: 16,
        lineHeight: 1.45,
      },
      buttonS: {
        fontFamily: "avenir_lt_book !important",
        fontSize: 12,
        lineHeight: 1.45,
      },
      bodyM: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 14,
        lineHeight: 1.45,
      },
      bodyS: {
        fontFamily: "avenir_lt_roman !important",
        fontSize: 12,
        lineHeight: 1.45,
      },
      dataXL: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 64,
        lineHeight: 1.2,
      },
      dataL: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 32,
        lineHeight: 1.2,
      },
      dataS: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 16,
        lineHeight: 1.3,
      },
      dataXXS: {
        fontFamily: "avenir_lt_light !important",
        fontSize: 12,
        lineHeight: 1.3,
      },
      caption: {
        fontFamily: "avenir_lt_roman !important",
        fontSize: 12,
        lineHeight: 1.45,
      },
    },
    components: {
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: radius,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: buttonRadius,
            textTransform: "none",
            borderColor: figmaColors.smooth,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: "capitalize",
            ...theme.typography.body2,
            fontFamily: "'Avenir roman', sans-serif",
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiTypography: {
        defaultProps: {
          fontFamily: "'Avenir book', sans-serif",
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontFamily: "'Avenir medium', sans-serif",
            color: theme.palette.text.primary,
            top: "-3px",
            "&.Mui-focused": {
              color: theme.palette.text.primary,
            },
          }),
          shrink: {
            fontSize: "18px",
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            borderBottom: `1px solid ${figmaColors.smooth}`,
          },
          input: {
            color: `1px solid ${figmaColors.shadow}`,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          arrow: ({ theme: { palette } }) => ({
            color: palette.secondary.main,
          }),
          tooltip: ({ theme: { palette } }) => ({
            backgroundColor: palette.secondary.main,
            color: palette.common.white,
            maxWidth: 210,
            padding: "8px 12px",
            fontSize: "12px",
            borderRadius: "16px",
          }),
        },
      },
      // Table
      MuiTableHead: {
        styleOverrides: {
          root: ({ theme: { palette } }) => ({
            backgroundColor: palette.grey[200],
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:hover td": {
              backgroundColor: theme.palette.success.main,
              cursor: "pointer",
            },
            "&:last-child td": {
              border: 0,
            },
            // when selected, the row is highlighted
            "&.Mui-selected": {
              backgroundColor: theme.palette.success.main,
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: ({ theme }) => ({
            fontSize: theme.typography.caption.fontSize,
            fontFamily: theme.typography.fontFamily,
            color: theme.palette.text.primary,
            borderTop: `2px solid ${theme.palette.grey[300]}`,
            padding: theme.spacing(1, 2),
          }),
          body: ({ theme }) => ({
            fontSize: theme.typography.caption.fontSize,
            fontFamily: theme.typography.caption.fontFamily,
            color: theme.palette.grey.A700,
            borderBottom: `1px dotted ${theme.palette.grey.A200}`,
            padding: theme.spacing(2, 2),
          }),
        },
      },
      // Accordion
      MuiAccordion: {
        defaultProps: {
          defaultExpanded: true,
          elevation: 0,
          disableGutters: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: "16px",
            // removes a weird border on the top of the accordion
            "&:before": {
              display: "none",
            },
            "&:first-of-type": {
              // The border radius of the first child is different!
              borderRadius: "16px",
            },
          }),
        },
      },
      MuiAccordionSummary: {
        defaultProps: {
          expandIcon: (
            <ExpandLessRounded fontSize={"small"} color={"secondary"} />
          ),
        },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.grey[200],
            // when not expanded, we show border radius on top and bottom
            // when expanded, we show border radius on top only (to match the background color of the accordion detail)
            "&.Mui-expanded": {
              borderRadius: theme.spacing(2, 2, 0, 0),
            },
            borderRadius: theme.spacing(2),

            // do not change the color of the summary box when focused
            // without this nested drawers with input boxes will end up focussing the summary box
            "&.Mui-focusVisible": {
              backgroundColor: theme.palette.grey[200],
            },
          }),
        },
      },
      MuiAccordionDetails: {
        styleOverrides: { root: { padding: 0 } },
      },
    },
  } as ThemeOptions);

  return {
    ...original,
    // @ts-ignore
    shadows: original.shadows.map((shadow, index) => {
      if (index === 3) {
        return "0px 0px 0px 0px rgba(0,0,0,0.18), 0px 0px 10px 0px rgba(0,0,0,0.12), 0px 0px 14px 2px rgba(0,0,0,0.10)";
      }
      return shadow;
    }),
  };
};

// Default Props for customer menu app for createDefaultTheme
export const CustomerMenuDefaultThemeProps = {
  mode: "light",
  backgroundColor: figmaColors.smooth,
};
